'use client';

import { type JSX, type ReactNode } from 'react';

import { JustifiedGallery, Pagination } from '@videoblocks/design-system';
import { WarningTriangle } from '@videoblocks/react-icons';

import { GenerationFlowLayout } from './GenerationFlow/GenerationFlowLayout';

interface SearchLayoutProps<T extends { aspectRatio?: number; id: number }> {
    results: T[];
    isLoading: boolean;
    error: string | null;
    hasSearched: boolean;
    currentPage: number;
    totalPages: number;
    goToPage: (page: number) => Promise<void>;
    noResultsTitle: string;
    noResultsIcon: ReactNode;
    renderCard: (item: T, aspectRatio: number) => ReactNode;
    resultsPerPage?: number;
    banner?: ReactNode;
    dataTestId?: string;
}

const ErrorState = (): JSX.Element => (
    <div data-testid="search-error" className="flex-1 flex flex-col">
        <GenerationFlowLayout
            title="Something went wrong"
            subtitle="This request couldn't be completed. Please try again"
            icon={
                <WarningTriangle
                    className="size-8 dark:[&_path]:fill-neutral-dark-1000"
                    data-testid="search-error-icon"
                />
            }
        />
    </div>
);

const NoResultsState = ({ title, icon }: { title: string; icon: ReactNode }): JSX.Element => (
    <div data-testid="search-no-results" className="flex-1 flex flex-col">
        <GenerationFlowLayout
            title={title}
            subtitle="Try a different search term or check your spelling."
            icon={icon}
        />
    </div>
);

export function SearchLayout<T extends { aspectRatio?: number; id: number }>({
    results,
    isLoading,
    error,
    hasSearched,
    currentPage,
    totalPages,
    goToPage,
    noResultsTitle,
    noResultsIcon,
    renderCard,
    resultsPerPage = 48,
    banner,
    dataTestId,
}: SearchLayoutProps<T>): JSX.Element {
    const showError = !isLoading && !!error;
    const showEmpty = !isLoading && !error && hasSearched && results.length === 0;

    return (
        <div className="flex flex-col gap-12 size-full justify-between" data-testid={dataTestId}>
            {banner}
            {showError ? (
                <ErrorState />
            ) : showEmpty ? (
                <NoResultsState title={noResultsTitle} icon={noResultsIcon} />
            ) : (
                <JustifiedGallery rowGapPx={8} columnGapPx={8} items={results} isLoading={isLoading}>
                    {({ item, aspectRatio }) => renderCard(item, aspectRatio)}
                </JustifiedGallery>
            )}
            {results.length > 0 && !showError && (
                <Pagination
                    resultsPerPage={resultsPerPage}
                    maxPage={totalPages}
                    currentPage={currentPage}
                    goTo={(page) => void goToPage(page)}
                />
            )}
        </div>
    );
}
